/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { DOCUMENT } from '@angular/common';
import type { Renderer2 } from '@angular/core';
import { Inject, Injectable, RendererFactory2, ViewEncapsulation, inject } from '@angular/core';
import type { LangEnum } from '@wizbii/utils/models';
import type { MetaDefinition } from '@angular/platform-browser';
import { Meta, Title } from '@angular/platform-browser';
import { ogImageByLang } from '@core/utils';

export interface ConfigMetaData {
  title: string;
  description: string;
  no_index?: boolean;
  image?: string;
}

interface MetaData {
  content: string;
  itemprop?: string;
  name?: string;
  property?: string;
}

@Injectable({
  providedIn: 'root',
})
export class MetaCustomService {
  configuration!: ConfigMetaData;

  private readonly renderer: Renderer2;
  private link: any;

  readonly #meta = inject(Meta);
  readonly #title = inject(Title);
  readonly #rendererFactory = inject(RendererFactory2);

  constructor(@Inject(DOCUMENT) private readonly document: any) {
    this.renderer = this.#rendererFactory.createRenderer(document, {
      id: Date.now().toString(),
      encapsulation: ViewEncapsulation.None,
      styles: [],
      data: {},
    });
  }

  setMetaData(config: ConfigMetaData): void {
    this.configuration = config;
    this.#title.setTitle(this.configuration.title);
    const tags = this.createTags(this.configuration);
    const computedTag: MetaDefinition = {};
    tags.forEach((tag: MetaData) => {
      Object.keys(tag).forEach((key) => {
        if (tag[key]) {
          computedTag[key] = tag[key];
        }
      });
      this.#meta.updateTag({ ...computedTag });
    });
  }

  removeTag(tagName: string): void {
    this.#meta.removeTag(tagName);
  }

  removeTags(): void {
    const tags = this.createTags(this.configuration);
    tags.forEach((tag) => {
      this.#meta.removeTag(`name="${tag.name}"`);
    });
  }

  private createTags(configuration?: ConfigMetaData): MetaData[] {
    const computedTags = [
      { itemprop: 'image', content: configuration?.image ?? '' },
      { name: 'description', content: configuration?.description ?? '' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:image', content: configuration?.image ?? '' },
      { name: 'twitter:title', content: configuration?.title ?? '' },
      { name: 'twitter:description', content: configuration?.description ?? '' },
      { property: 'og:title', content: configuration?.title ?? '' },
      { property: 'og:description', content: configuration?.description ?? '' },
      { property: 'og:image', content: configuration?.image ?? '' },
    ];

    return configuration?.no_index ? computedTags.concat([{ name: 'robots', content: 'noindex' }]) : computedTags;
  }

  updateOrAddCanonicalURL(url?: string): void {
    const canUrl = url
      ? url
      : [window.location.protocol, '//', window.location.host, window.location.pathname].join('');

    if (!this.link) {
      this.link = this.renderer.createElement('link');
      this.link.setAttribute('rel', 'canonical');
      this.link.setAttribute('href', canUrl);
      this.renderer.appendChild(this.document.head, this.link);
      return;
    }

    this.link.setAttribute('href', canUrl);
  }

  deleteCanonicalURL(): void {
    if (!this.link) {
      return;
    }

    this.renderer.removeChild(this.document.head, this.link);
  }

  updateOrAddTag(meta: MetaDefinition): void {
    if (!meta) {
      return;
    }

    const id = Object.keys(meta)[0];

    if (Object.keys(meta).includes('id') && this.#meta.getTag(`${id}="${meta[id]}"`)) {
      this.#meta.updateTag(meta, `${id}="${meta[id]}"`);
      return;
    }

    const key = Object.keys(meta)[0];
    if (this.#meta.getTag(`${key}="${meta[key]}"`)) {
      this.#meta.updateTag(meta, `${key}="${meta[key]}"`);
      return;
    }

    this.#meta.addTag(meta);
  }

  setPageMetadata(pageMetaData: ConfigMetaData, lang: LangEnum): void {
    this.setMetaData(pageMetaData);
    this.updateOrAddTag({
      property: 'og:title',
      content: pageMetaData.title,
    });
    this.updateOrAddTag({
      property: 'og:description',
      content: pageMetaData.description,
    });

    const ogImageUrl = ogImageByLang[lang];
    if (ogImageUrl) {
      this.updateOrAddTag({ property: 'og:image', content: ogImageUrl });
      this.updateOrAddTag({ name: 'twitter:image', content: ogImageUrl });
    }
  }
}
